import React, { useEffect, useRef, useState, useCallback } from 'react'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import type { GlobalSearchResponse, GlobalSearchResult } from '@ztechapps/common.contract'
import { GlobalSearchResultGroup } from './GlobalSearchResultGroup'
import { Spinner } from 'react-bootstrap'
import { Icon } from '@ztechapps/webui.util'

export type GlobalSearchResultsDropdownProps = {
  results: GlobalSearchResponse | undefined
  loading: boolean
  error: any
  show: boolean
  onResultClick: (result: GlobalSearchResult) => void
  onClose: () => void
  searchQuery: string
  /** Group headings by result module; a module with no entry prints its raw name. */
  moduleLabels?: Record<string, string>
}

export const GlobalSearchResultsDropdown = ({
  results,
  loading,
  error,
  show,
  onResultClick,
  onClose,
  searchQuery,
  moduleLabels,
}: GlobalSearchResultsDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)

  // Calculate total number of results for keyboard navigation
  const totalResults = results?.groups.reduce((acc, group) => acc + group.results.length, 0) ?? 0

  // Get result by absolute index
  const getResultByIndex = useCallback(
    (index: number): GlobalSearchResult | null => {
      if (!results) return null

      let currentIndex = 0
      for (const group of results.groups) {
        if (index < currentIndex + group.results.length) {
          return group.results[index - currentIndex]
        }
        currentIndex += group.results.length
      }
      return null
    },
    [results]
  )

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!show || totalResults === 0) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((prev) => {
          if (prev === null) return 0
          return prev < totalResults - 1 ? prev + 1 : prev
        })
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((prev) => {
          if (prev === null || prev === 0) return null
          return prev - 1
        })
      } else if (e.key === 'Enter' && highlightedIndex !== null) {
        e.preventDefault()
        const result = getResultByIndex(highlightedIndex)
        if (result) {
          onResultClick(result)
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }

    if (show) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [show, highlightedIndex, totalResults, onResultClick, onClose, getResultByIndex])

  // Reset highlighted index when results change
  useEffect(() => {
    setHighlightedIndex(null)
  }, [results])

  if (!show) {
    return null
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="p-4 text-center">
          <Spinner animation="border" size="sm" className="me-2" />
          <span>Searching...</span>
        </div>
      )
    }

    if (error) {
      return (
        <div className="p-4 text-center text-danger">
          <Icon name="exclamation-triangle" className="me-2" />
          <span>Error: {error.message || 'Failed to search'}</span>
        </div>
      )
    }

    if (!results || isEmpty(results.groups) || results.totalResults === 0) {
      return (
        <div className="p-4 text-center text-muted">
          <Icon name="search" className="me-2" />
          <span>No results found for "{searchQuery}"</span>
          <div className="small mt-2">Try checking your spelling or using different keywords</div>
        </div>
      )
    }

    return (
      <div>
        {map(results.groups, (group) => {
          return (
            <GlobalSearchResultGroup
              key={group.module}
              group={group}
              label={moduleLabels?.[group.module]}
              onResultClick={onResultClick}
            />
          )
        })}
        {results.totalResults > 0 && (
          <div className="p-2 text-center small text-muted border-top">
            Found {results.totalResults} total {results.totalResults === 1 ? 'result' : 'results'}
            {!isEmpty(results.executionTimeMs) && <span> in {results.executionTimeMs}ms</span>}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      ref={dropdownRef}
      className="position-absolute bg-body border rounded shadow-lg"
      style={{
        top: '100%',
        left: 0,
        right: 0,
        maxHeight: '500px',
        overflowY: 'auto',
        zIndex: 1050,
        marginTop: '4px',
      }}
    >
      {renderContent()}
    </div>
  )
}
